import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  TemplateVersion,
  ReferenceEditorItem,
  ReferenceRequirement,
} from '../models';

const REQUIREMENT_VALUE_MAP: Record<ReferenceRequirement, string> = {
  Obligatoire: 'MandatoryDeliverable',
  'Activité requise': 'RequiredActivity',
  Recommandé: 'Recommended',
  'Selon besoin': 'AsNeeded',
  'Non requis': 'NotRequired',
};

const REQUIREMENT_LABEL_MAP: Record<string, ReferenceRequirement> = {
  MandatoryDeliverable: 'Obligatoire',
  RequiredActivity: 'Activité requise',
  Recommended: 'Recommandé',
  AsNeeded: 'Selon besoin',
  NotRequired: 'Non requis',
};

const REQUIREMENT_BY_INDEX: Record<number, ReferenceRequirement> = {
  0: REQUIREMENT_LABEL_MAP['MandatoryDeliverable'],
  1: REQUIREMENT_LABEL_MAP['RequiredActivity'],
  2: REQUIREMENT_LABEL_MAP['Recommended'],
  3: REQUIREMENT_LABEL_MAP['AsNeeded'],
  4: REQUIREMENT_LABEL_MAP['NotRequired'],
};

@Injectable({ providedIn: 'root' })
export class TemplateService {
  private readonly apiUrl = environment.apiUrl;

  constructor(private http: HttpClient) {}

  getAll(): Observable<TemplateVersion[]> {
    return this.http.get<TemplateVersion[]>(`${this.apiUrl}/template-versions`);
  }

  getPublished(): Observable<TemplateVersion> {
    return this.http.get<TemplateVersion>(`${this.apiUrl}/template-versions/published`);
  }

  getById(id: string): Observable<TemplateVersion> {
    return this.http.get<TemplateVersion>(`${this.apiUrl}/template-versions/${id}`);
  }

  getTemplates(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/templates`);
  }

  createDraft(sourceVersionId: string | null): Observable<TemplateVersion> {
    return this.http.post<TemplateVersion>(`${this.apiUrl}/template-versions`, {
      sourceVersionId,
    });
  }

  deleteDraft(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/template-versions/${id}`);
  }

  updateDraft(
    id: string,
    items: ReferenceEditorItem[],
    frameworks: string[]
  ): Observable<TemplateVersion> {
    const payload = {
      items: items.map((item, index) => ({
        id: item.id ?? null,
        phase: item.phase,
        title: item.title,
        kind: item.kind,
        requirements: Object.fromEntries(
          frameworks.map(fw => [
            fw,
            REQUIREMENT_VALUE_MAP[item.requirements[fw] ?? 'Non requis'],
          ])
        ),
        helpText: item.helpText || null,
        referenceUrl: item.referenceUrl || null,
      })),
    };
    return this.http.put<TemplateVersion>(
      `${this.apiUrl}/template-versions/${id}`,
      payload
    );
  }

  publish(id: string): Observable<TemplateVersion> {
    return this.http.post<TemplateVersion>(
      `${this.apiUrl}/template-versions/${id}/publish`,
      {}
    );
  }

  // ── Mapping helpers ────────────────────────────────────────────────────────

  /**
   * Converts raw API rules array into a Record<frameworkCode, ReferenceRequirement>
   * suitable for the reference editor.
   */
  mapRequirements(
    rules: any[],
    frameworks: string[]
  ): Record<string, ReferenceRequirement> {
    return Object.fromEntries(
      frameworks.map(fw => {
        // The database stores enum names, but accepting the other JSON forms
        // keeps the screen faithful to the API during deployments/migrations.
        const rule = rules.find(rule =>
          String(rule?.frameworkCode ?? rule?.FrameworkCode ?? '').trim()
            .toUpperCase() === fw.trim().toUpperCase());
        return [fw, this.requirementLabel(rule?.requirement ?? rule?.Requirement)];
      })
    );
  }

  updatePublishedItemDetails(
    versionId: string,
    itemId: string,
    helpText?: string,
    referenceUrl?: string
  ): Observable<TemplateVersion> {
    return this.http.patch<TemplateVersion>(
      `${this.apiUrl}/template-versions/${versionId}/items/${itemId}/details`,
      { helpText: helpText || null, referenceUrl: referenceUrl || null }
    );
  }

  private requirementLabel(value: unknown): ReferenceRequirement {
    if (typeof value === 'number') {
      return REQUIREMENT_BY_INDEX[value] ?? 'Non requis';
    }

    const raw = String(value ?? '').trim();
    if (REQUIREMENT_LABEL_MAP[raw]) {
      return REQUIREMENT_LABEL_MAP[raw];
    }

    const normalized = raw.toLowerCase().replace(/[ _-]/g, '');
    const labels: Record<string, ReferenceRequirement> = {
      obligatoire: REQUIREMENT_LABEL_MAP['MandatoryDeliverable'],
      mandatorydeliverable: REQUIREMENT_LABEL_MAP['MandatoryDeliverable'],
      activiterequise: REQUIREMENT_LABEL_MAP['RequiredActivity'],
      requiredactivity: REQUIREMENT_LABEL_MAP['RequiredActivity'],
      recommande: REQUIREMENT_LABEL_MAP['Recommended'],
      recommended: REQUIREMENT_LABEL_MAP['Recommended'],
      selonbesoin: REQUIREMENT_LABEL_MAP['AsNeeded'],
      asneeded: REQUIREMENT_LABEL_MAP['AsNeeded'],
      nonrequis: REQUIREMENT_LABEL_MAP['NotRequired'],
      notrequired: REQUIREMENT_LABEL_MAP['NotRequired'],
    };
    return labels[normalized.normalize('NFD').replace(/\p{Diacritic}/gu, '')]
      ?? 'Non requis';
  }

  /**
   * Converts a TemplateVersion's items into ReferenceEditorItems
   * ready for the settings/reference editor UI.
   */
  toEditorItems(
    version: TemplateVersion,
    frameworks: string[]
  ): ReferenceEditorItem[] {
    return version.items.map((item, index) => ({
      localId: item.id ?? `item-${index}`,
      id: item.id,
      phase: item.sectionName,
      title: item.title,
      kind: item.activityKind,
      requirements: this.mapRequirements(item.rules ?? [], frameworks),
      helpText: item.helpText,
      referenceUrl: item.referenceUrl,
    }));
  }
}
